import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { studentsApi } from './api';

const REQUIRED_FIELDS = [
  'Scholar Number',
  'First Name',
  'Last Name',
  'Class',
  'Section',
  'Academic Session',
];

const StudentBulkUpload: React.FC = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  const [error, setError] = useState('');

  const acceptedTypes = useMemo(
    () => '.xlsx,.xls,.csv',
    [],
  );

  const downloadTemplate = async () => {
    try {
      const blob = await studentsApi.downloadBulkTemplate();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'student-bulk-upload-template.xlsx';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      setError('Failed to download template');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setError('');
    setResult(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setError('');
      const response = await studentsApi.bulkUpload(selectedFile);
      setResult(response);
    } catch (uploadError: any) {
      const message =
        uploadError?.response?.data?.message ||
        uploadError?.response?.data?.error ||
        'Bulk upload failed';
      setError(Array.isArray(message) ? message.join(', ') : message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/scholar-register/students')} sx={{ mb: 1 }}>
            Back to Students
          </Button>
          <Typography variant="h4" fontWeight={700}>
            Student Bulk Upload
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Download the Excel template, fill the mandatory fields, and upload student records in one step.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
            Download Template
          </Button>
          <Button variant="contained" startIcon={<UploadFileIcon />} onClick={handleUpload} disabled={uploading}>
            {uploading ? 'Uploading...' : 'Upload File'}
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Template Notes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Mandatory fields are highlighted in the Excel template. The first demo row is filled with sample data and is ignored automatically on upload.
            </Typography>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }} useFlexGap>
              {REQUIRED_FIELDS.map((field) => (
                <Chip key={field} label={field} color="error" variant="outlined" sx={{ mb: 1 }} />
              ))}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              File Rules
            </Typography>
            <Stack spacing={1} sx={{ color: 'text.secondary', fontSize: 14 }}>
              <Box>• Supported formats: Excel (.xlsx/.xls) and CSV (.csv)</Box>
              <Box>• Academic Session must be the active session name</Box>
              <Box>• Dates should be entered as YYYY-MM-DD</Box>
              <Box>• RTE, BPL, and Is Declared accept Yes/No or True/False</Box>
              <Box>• Admission numbers are generated automatically</Box>
              <Box>• The demo row in the template is safe to leave as-is because it will not be imported</Box>
            </Stack>

            <Box sx={{ mt: 3, p: 2, borderRadius: 2, border: '1px dashed', borderColor: 'divider', textAlign: 'center' }}>
              <CloudUploadIcon color="primary" sx={{ fontSize: 42, mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={700}>
                Select a file to begin
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a prepared template and upload it from the panel on the right.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Upload Student File
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload the completed template to create student records in bulk.
            </Typography>

            <Box
              sx={{
                border: '1px dashed',
                borderColor: selectedFile ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                bgcolor: selectedFile ? 'action.hover' : 'background.default',
              }}
            >
              <input
                type="file"
                accept={acceptedTypes}
                id="student-bulk-upload-file"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <label htmlFor="student-bulk-upload-file">
                <Button component="span" variant="contained" startIcon={<UploadFileIcon />}>
                  Choose File
                </Button>
              </label>

              <Typography sx={{ mt: 2 }}>
                {selectedFile ? selectedFile.name : 'No file selected'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedFile ? `${(selectedFile.size / 1024).toFixed(1)} KB` : 'Excel or CSV file only'}
              </Typography>

              {uploading && <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />}
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {result && (
              <Box sx={{ mt: 3 }}>
                <Alert severity={result.failedCount ? 'warning' : 'success'} sx={{ mb: 2 }}>
                  {result.createdCount} students imported successfully{result.failedCount ? `, ${result.failedCount} row(s) failed` : ''}.
                </Alert>

                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
                  <Chip label={`Total rows: ${result.totalRows}`} />
                  <Chip color="success" label={`Created: ${result.createdCount}`} />
                  <Chip color="error" label={`Failed: ${result.failedCount}`} />
                </Stack>

                {Array.isArray(result.errors) && result.errors.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 240, overflow: 'auto' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      Row Errors
                    </Typography>
                    <Stack spacing={1}>
                      {result.errors.map((entry: any) => (
                        <Alert key={`${entry.row}-${entry.message}`} severity="error">
                          Row {entry.row}: {entry.message}
                        </Alert>
                      ))}
                    </Stack>
                  </Paper>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default StudentBulkUpload;